import {
  Constants,
  USER_ROLES,
  UserUtilities,
} from '@pwc/pwc-deals-web-common';
import decode from 'jwt-decode';
import _keys from 'lodash/keys';
import _get from 'lodash/get';
import _some from 'lodash/some';
import featureFlags from 'common/helpers/featureFlags';

import { CLIENT_TOKEN_KEY, PWC_USER_TOKEN_KEY } from './authorizationService';

const applicationTokenKey = 'jwtToken';
const projectTokenKeyPrefix = 'projectJWT';
const projectTokenKeyTemplate = `${projectTokenKeyPrefix}{PROJ}`;

const getProjectToken = (projectId, userRole) => {
  const tokenStorageKey = projectTokenKeyTemplate.replace(
    '{PROJ}',
    btoa(projectId)
  );

  if (userRole && featureFlags.isGlobalToggleEnabled) {
    if (userRole === USER_ROLES.CLIENT) {
      return sessionStorage.getItem(`${CLIENT_TOKEN_KEY}${tokenStorageKey}`);
    }
    if (userRole === USER_ROLES.PWC_USER) {
      return sessionStorage.getItem(`${PWC_USER_TOKEN_KEY}${tokenStorageKey}`);
    }
  }
  return sessionStorage.getItem(tokenStorageKey);
};

const getUserTerritory = () => {
  const token = decode(sessionStorage.getItem(applicationTokenKey));
  return _get(token, 'country');
};

const isActiveAnalyticsTerritory = () => {
  try {
    const blackListCountryCodes = [Constants.TERRITORY_CODES.GERMANY];
    const countryCode = getUserTerritory();
    return countryCode
      ? blackListCountryCodes.indexOf(countryCode) === -1
      : null;
  } catch (e) {
    return null;
  }
};

const isKeyJunctionRelated = (key) =>
  key.startsWith(projectTokenKeyPrefix) || key === applicationTokenKey;

const isUserInternal = () => UserUtilities.isUserInternal();

export const getAllTokenKeys = () =>
  _keys(sessionStorage).filter(isKeyJunctionRelated);

export const getAllTokens = () =>
  getAllTokenKeys().map((key) => sessionStorage.getItem(key));

export const clearAllTokens = () =>
  getAllTokenKeys().forEach((key) => sessionStorage.removeItem(key));

export const getPlaintextToken = (projectId, userRole) => {
  const token = getProjectToken(projectId, userRole);
  if (token) {
    return decode(token);
  }
  return null;
};

const hasProjectPermission = (projectId, permissionList = [], userRole) => {
  const permissions = getPlaintextToken(projectId, userRole);
  const permissionKeys = _keys(permissions);

  return _some(permissionKeys, (key) => permissionList.includes(key));
};

const hasProjectTokenProp = (projectId, prop) => {
  let hasToken = false;
  const tokenStorageKey = projectTokenKeyTemplate.replace(
    '{PROJ}',
    btoa(projectId)
  );
  const savedToken = sessionStorage.getItem(tokenStorageKey);

  if (savedToken) {
    const token = decode(savedToken);

    prop.forEach((p) => {
      if (Object.prototype.hasOwnProperty.call(token, p)) {
        hasToken = true;
      }
    });
  }

  return hasToken;
};

const getProjectTokenProperty = (projectId, prop, userRole) => {
  const token = getPlaintextToken(projectId, userRole);
  return token && token[prop];
};

const hasProjectRoles = (projectId, roles) => {
  const projectRole = getProjectTokenProperty(projectId, 'role');
  return roles.includes(projectRole);
};

const getProjectRole = (projectId) => {
  return getProjectTokenProperty(projectId, 'role');
};

const isPwcReadOnlyRole = (projectId) => {
  try {
    return Boolean(
      hasProjectRoles(projectId, [
        Constants.USER_ROLES.PWC_READONLY_DISPLAY,
        Constants.USER_ROLES.PWC_READONLY,
      ])
    );
  } catch (e) {
    return false;
  }
};

const getUserEmail = () => {
  const token = decode(sessionStorage.getItem(applicationTokenKey));
  return _get(token, 'email');
};

const getUserId = () => {
  const token = decode(sessionStorage.getItem(applicationTokenKey));
  return _get(token, 'id');
};

const userTokenService = {
  getProjectToken,
  hasProjectPermission,
  hasProjectTokenProp,
  getProjectTokenProperty,
  hasProjectRoles,
  getProjectRole,
  getUserEmail,
  isUserInternal,
  isPwcReadOnlyRole,
  getUserTerritory,
  isActiveAnalyticsTerritory,
  getUserId,
};

export default userTokenService;
